import { useState, useEffect, useRef } from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { X, Download, Smartphone, Monitor, GripVertical } from 'lucide-react';

export default function InstallPWA() {
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  
  // Drag and drop state
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef(null);
  const dragOffset = useRef({ x: 0, y: 0 });

  // Handle drag
  const handleMouseDown = (e) => {
    if (dragRef.current) {
      setIsDragging(true);
      const rect = dragRef.current.getBoundingClientRect();
      dragOffset.current = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      };
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && dragRef.current) {
      const newX = e.clientX - dragOffset.current.x;
      const newY = e.clientY - dragOffset.current.y;
      
      // Keep within viewport
      const maxX = window.innerWidth - dragRef.current.offsetWidth;
      const maxY = window.innerHeight - dragRef.current.offsetHeight;
      
      setPosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY))
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Touch handlers for mobile
  const handleTouchStart = (e) => {
    if (dragRef.current) {
      setIsDragging(true);
      const touch = e.touches[0];
      const rect = dragRef.current.getBoundingClientRect();
      dragOffset.current = {
        x: touch.clientX - rect.left,
        y: touch.clientY - rect.top
      };
    }
  };

  const handleTouchMove = (e) => {
    if (isDragging && dragRef.current) {
      const touch = e.touches[0];
      const newX = touch.clientX - dragOffset.current.x;
      const newY = touch.clientY - dragOffset.current.y;
      
      const maxX = window.innerWidth - dragRef.current.offsetWidth;
      const maxY = window.innerHeight - dragRef.current.offsetHeight;
      
      setPosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY))
      });
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchmove', handleTouchMove);
      window.addEventListener('touchend', handleTouchEnd);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDragging]);

  useEffect(() => {
    // Check if already installed
    const isInStandaloneMode = window.matchMedia('(display-mode: standalone)').matches || 
                               window.navigator.standalone || 
                               document.referrer.includes('android-app://');
    
    setIsStandalone(isInStandaloneMode);

    // Check platform
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    const desktop = window.innerWidth >= 1024;
    setIsIOS(iOS);
    setIsDesktop(desktop);
    
    // Set initial position based on platform
    if (desktop) {
      setPosition({ x: window.innerWidth - 420, y: window.innerHeight - 250 });
    } else {
      setPosition({ x: 8, y: 60 }); // Poziție sus pe mobil, sub header
    }

    // Listen for install prompt (works on Android Chrome, Desktop Chrome, Edge)
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallPrompt(true);
    };

    window.addEventListener('beforeinstallprompt', handler);

    // Show iOS/Desktop prompt if not installed
    if (!isInStandaloneMode) {
      const dismissed = localStorage.getItem('pwa-install-dismissed');
      const dismissedTime = localStorage.getItem('pwa-install-dismissed-time');
      
      // Show again after 7 days
      if (dismissed && dismissedTime) {
        const daysSinceDismissed = (Date.now() - parseInt(dismissedTime)) / (1000 * 60 * 60 * 24);
        if (daysSinceDismissed > 7) {
          localStorage.removeItem('pwa-install-dismissed');
          localStorage.removeItem('pwa-install-dismissed-time');
          if (iOS || desktop) {
            setShowInstallPrompt(true);
          }
        }
      } else if (!dismissed) {
        if (iOS || desktop) {
          setShowInstallPrompt(true);
        }
      }
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
    };
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      console.log(`User response: ${outcome}`);
      setDeferredPrompt(null);
      setShowInstallPrompt(false);
      if (outcome === 'accepted') {
        localStorage.setItem('pwa-install-accepted', 'true');
      }
    }
  };

  const handleDismiss = () => {
    setShowInstallPrompt(false);
    localStorage.setItem('pwa-install-dismissed', 'true');
    localStorage.setItem('pwa-install-dismissed-time', Date.now().toString());
  };

  // Don't show if already installed
  if (isStandalone || !showInstallPrompt) {
    return null;
  }

  return (
    <div 
      ref={dragRef}
      className="fixed z-50 max-w-sm touch-none select-none"
      style={{
        left: position.x,
        top: position.y,
        cursor: isDragging ? 'grabbing' : 'grab'
      }}
      data-testid="pwa-install-prompt"
    >
      <Alert className="bg-gradient-to-r from-teal-500 to-blue-600 text-white border-0 shadow-2xl rounded-xl">
        {/* Drag handle */}
        <div 
          className="flex items-center justify-center py-1 mb-2 border-b border-white/20 cursor-grab active:cursor-grabbing"
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
        >
          <GripVertical className="w-5 h-5 text-white/60" />
          <span className="text-xs text-white/60 ml-1">Trage pentru a muta</span>
        </div>
        
        <div className="flex items-start gap-3">
          {isDesktop ? <Monitor className="w-5 h-5 mt-0.5" /> : <Smartphone className="w-5 h-5 mt-0.5" />}
          <div className="flex-1">
            <AlertDescription className="text-white">
              {isIOS ? (
                <div className="space-y-2">
                  <p className="font-semibold">📱 Instalează e-Tesa pe iPhone!</p>
                  <ol className="text-sm space-y-1">
                    <li>1️⃣ Apasă butonul <strong>Share</strong> (📤) din bara de jos</li>
                    <li>2️⃣ Scroll în jos și selectează <strong>"Add to Home Screen"</strong></li>
                    <li>3️⃣ Apasă <strong>"Add"</strong> pentru confirmare</li>
                  </ol>
                </div>
              ) : isDesktop ? (
                <div className="space-y-2">
                  <p className="font-semibold">💻 Instalează e-Tesa pe Desktop!</p>
                  <p className="text-sm">Adaugă aplicația pentru acces rapid din desktop.</p>
                  <Button 
                    onClick={handleInstallClick}
                    className="w-full mt-2 bg-white text-teal-600 hover:bg-slate-100"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Instalează pe Desktop
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="font-semibold">📱 Instalează e-Tesa pe telefon!</p>
                  <p className="text-sm">Adaugă aplicația pe ecranul principal pentru acces rapid.</p>
                  <Button 
                    onClick={handleInstallClick}
                    className="w-full mt-2 bg-white text-teal-600 hover:bg-slate-100"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Instalează Acum
                  </Button>
                </div>
              )}
            </AlertDescription>
          </div>
          <button
            onClick={handleDismiss}
            className="text-white hover:text-slate-200 transition-colors"
            aria-label="Close"
            data-testid="close-pwa-prompt"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </Alert>
    </div>
  );
}
